import React from "react";
import type { QuickFilterState } from "@/components/shared/DataTable-modular/types";
import type { SimpleTableStateSetupResult } from "./SimpleTableStateSetup";

/**
 * Create wrapper callbacks that adapt React state setters to match the context interface.
 * These wrappers maintain the exact same behavior while providing a consistent interface.
 */
export function createSimpleTableCallbackWrappers(stateSetup: SimpleTableStateSetupResult) {
  const {
    setSortColumns,
    setSortField,
    setSortDirection,
    setSelectedItems,
    setColumnWidths,
    setEditingCell,
    setEditValue,
    setDraggedColumn,
    setShowFloatingBulkActions,
    setColumnOrder,
  } = stateSetup;

  // Wrap setSortColumns to match context interface
  const wrappedSetSortColumns = React.useCallback(
    (columns: Array<{ field: string; direction: "asc" | "desc"; priority: number }>) => {
      setSortColumns(columns);
    },
    [setSortColumns]
  );

  // Wrap setSortField to match context interface
  const wrappedSetSortField = React.useCallback(
    (field: string) => {
      setSortField(field);
    },
    [setSortField]
  );

  // Wrap setSortDirection to match context interface
  const wrappedSetSortDirection = React.useCallback(
    (direction: "asc" | "desc") => {
      setSortDirection(direction);
    },
    [setSortDirection]
  );

  // Wrap setSelectedItems to match context interface
  const wrappedSetSelectedItems = React.useCallback(
    (items: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setSelectedItems(items);
    },
    [setSelectedItems]
  );

  // Wrap setColumnWidths to match context interface
  const wrappedSetColumnWidths = React.useCallback(
    (
      widths: Record<string, number> | ((prev: Record<string, number>) => Record<string, number>)
    ) => {
      setColumnWidths(widths);
    },
    [setColumnWidths]
  );

  // Wrap setEditingCell to match context interface
  const wrappedSetEditingCell = React.useCallback(
    (cell: { rowId: string; fieldId: string } | null) => {
      setEditingCell(cell);
    },
    [setEditingCell]
  );

  // Wrap setEditValue to match context interface
  const wrappedSetEditValue = React.useCallback(
    (value: any) => {
      setEditValue(value);
    },
    [setEditValue]
  );

  // Wrap setDraggedColumn to match context interface
  const wrappedSetDraggedColumn = React.useCallback(
    (column: string | null) => {
      setDraggedColumn(column);
    },
    [setDraggedColumn]
  );

  // Wrap setShowFloatingBulkActions to match context interface
  const wrappedSetShowFloatingBulkActions = React.useCallback(
    (show: boolean) => {
      setShowFloatingBulkActions(show);
    },
    [setShowFloatingBulkActions]
  );

  // Wrap setColumnOrder to match context interface
  const wrappedSetColumnOrder = React.useCallback(
    (order: string[]) => {
      setColumnOrder(order);
    },
    [setColumnOrder]
  );

  return {
    wrappedSetSortColumns,
    wrappedSetSortField,
    wrappedSetSortDirection,
    wrappedSetSelectedItems,
    wrappedSetColumnWidths,
    wrappedSetEditingCell,
    wrappedSetEditValue,
    wrappedSetDraggedColumn,
    wrappedSetShowFloatingBulkActions,
    wrappedSetColumnOrder,
  };
}

export type SimpleTableCallbackWrappersResult = ReturnType<
  typeof createSimpleTableCallbackWrappers
>;
